"use client";

import RootNotFound from "@/app/not-found";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronsRight,
  Copy,
  Share2,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  normalizeLocale,
  sitePageUrl,
  ogLocaleTag,
} from "@/lib/site";

/* ------------------------------------------------------------------ */
/* API                                                                 */
/* ------------------------------------------------------------------ */

async function getBlog(slug) {
  try {
    const res = await fetch(
      `/api/blogs/${encodeURIComponent(slug)}`
    );

    if (!res.ok) return null;

    const data = await res.json();

    return data?.success ? data.blog : null;
  } catch (error) {
    console.error("Error fetching blog:", error);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Design Tokens                                                       */
/* ------------------------------------------------------------------ */

const colors = {
  gray950: "#0a0a0a",
  gray900: "#171717",
  gray800: "#262626",
  gray700: "#374151",
  gray600: "#4b5563",
  gray500: "#6b7280",
  gray400: "#9ca3af",
  gray300: "#d1d5db",
  gray200: "#e5e7eb",
  gray100: "#f3f4f6",
  gray50: "#f9fafb",

  red600: "#d91f26",
  red50: "#fff1f2",

  blue50: "#fff1f2",
  blue200: "#fecdd3",
};

const brandGradient =
  "linear-gradient(90deg, #d91f26 0%, #ef4444 100%)";

const brandGradientTextStyle = {
  backgroundImage: brandGradient,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

function lineClamp(n) {
  return {
    display: "-webkit-box",
    WebkitLineClamp: n,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function formatBlogDate(date) {
  if (!date) return "";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return date;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function slugifyHeading(text = "") {
  return (
    String(text)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "section"
  );
}

function withHeadingIds(blocks = []) {
  const usedIds = new Map();

  return blocks.map((block) => {
    if (block.type !== "heading") {
      return block;
    }

    const baseId = slugifyHeading(block.text);

    const count =
      usedIds.get(baseId) || 0;

    usedIds.set(
      baseId,
      count + 1
    );

    const id =
      count === 0
        ? baseId
        : `${baseId}-${count + 1}`;

    return {
      ...block,
      id,
    };
  });
}

function buildToc(blocks = []) {
  return blocks
    .filter(
      (block) =>
        block.type === "heading" &&
        block.text?.trim()
    )
    .map((block) => ({
      level: block.level || 2,
      text: block.text.trim(),
      id: block.id,
    }));
}

function nestToc(flatItems = []) {
  const roots = [];

  let current = null;

  flatItems.forEach((item) => {
    if (
      item.level <= 2 ||
      !current
    ) {
      current = {
        ...item,
        children: [],
      };

      roots.push(current);
    } else {
      current.children.push(item);
    }
  });

  return roots;
}

function scrollToHeading(id) {
  const target =
    document.getElementById(id);

  if (!target) return;

  const siteHeaderHeight =
    window.innerWidth < 768
      ? 72
      : 88;

  window.scrollTo({
    top:
      target.getBoundingClientRect().top +
      window.scrollY -
      siteHeaderHeight -
      24,

    behavior: "smooth",
  });
}

/* ------------------------------------------------------------------ */
/* Inline Content                                                      */
/* ------------------------------------------------------------------ */

function renderInlineContent(text) {
  if (!text) {
    return null;
  }

  const source = String(text);

  const parts = [];

  const regex =
    /\*\*([^*]+)\*\*|\*([^*]+)\*|__([^_]+)__|_([^_]+)_|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>"]+)/g;

  let last = 0;
  let match;

  while (
    (match = regex.exec(source)) !==
    null
  ) {
    if (match.index > last) {
      parts.push(
        source.slice(
          last,
          match.index
        )
      );
    }

    if (
      match[1] !== undefined ||
      match[3] !== undefined
    ) {
      const value =
        match[1] ?? match[3];

      parts.push(
        <strong
          key={match.index}
          style={{
            fontWeight: 700,
            color: colors.gray950,
          }}
        >
          {/* Recurse so links inside bold/italic (e.g. **[text](url)**) still render */}
          {renderInlineContent(value)}
        </strong>
      );
    } else if (
      match[2] !== undefined ||
      match[4] !== undefined
    ) {
      const value =
        match[2] ?? match[4];

      parts.push(
        <em
          key={match.index}
          style={{
            fontStyle: "italic",
          }}
        >
          {renderInlineContent(value)}
        </em>
      );
    } else {
      const label =
        match[5] ??
        match[7];

      const href =
        match[6] ??
        match[7];

      const external =
        /^https?:\/\//i.test(
          href
        );

      parts.push(
        <a
          key={match.index}
          href={href}
          {...(
            external
              ? {
                  target:
                    "_blank",

                  rel:
                    "noopener noreferrer",
                }
              : {}
          )}
          style={{
            ...brandGradientTextStyle,
            fontWeight: 600,
            textDecoration:
              "underline",
            textUnderlineOffset:
              4,
          }}
        >
          {label}
        </a>
      );
    }

    last =
      match.index +
      match[0].length;
  }

  if (
    last <
    source.length
  ) {
    parts.push(
      source.slice(last)
    );
  }

  return parts.length
    ? parts
    : source;
}

/* ------------------------------------------------------------------ */
/* Share Menu                                                          */
/* ------------------------------------------------------------------ */

function ShareLinks({
  title,
}) {
  const [url, setUrl] =
    useState("");

  const [open, setOpen] =
    useState(false);

  const [copied, setCopied] =
    useState(false);

  const ref =
    useRef(null);

  useEffect(() => {
    setUrl(
      window.location.href
    );
  }, []);

  useEffect(() => {
    if (!open) return;

    function closeOnOutsideClick(
      event
    ) {
      if (
        ref.current &&
        !ref.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    }

    document.addEventListener(
      "mousedown",
      closeOnOutsideClick
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        closeOnOutsideClick
      );
  }, [open]);

  if (!url) return null;

  const encodedUrl =
    encodeURIComponent(url);

  const encodedTitle =
    encodeURIComponent(
      title || ""
    );

  const links = [
    {
      label: "X (Twitter)",

      href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },

    {
      label: "Facebook",

      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },

    {
      label: "LinkedIn",

      href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
    },

    {
      label: "WhatsApp",

      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    },
  ];

  function handleCopy() {
    navigator.clipboard?.writeText(
      url
    );

    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1800);
  }

  return (
    <div
      ref={ref}
      className="relative inline-block"
      style={{
        zIndex: 20,
      }}
    >
      <button
        type="button"
        onClick={() =>
          setOpen(
            (prev) =>
              !prev
          )
        }
        aria-label="Share this article"
        className="flex h-9 w-9 items-center justify-center rounded-full border bg-white transition hover:border-gray-400 hover:text-black"
        style={{
          borderColor:
            colors.gray200,

          color:
            colors.gray500,
        }}
      >
        <Share2 className="h-4 w-4" />
      </button>

      {open && (
        <>
          {/* Mobile overlay */}

          <div
            className="fixed inset-0 bg-black/40 sm:hidden"
            style={{
              zIndex: 25,
            }}
            onClick={() =>
              setOpen(false)
            }
          />

          {/* Share box */}

          <div
            className="
              fixed
              bottom-0
              left-0
              right-0
              overflow-hidden
              rounded-t-2xl
              bg-white
              shadow-2xl

              sm:absolute
              sm:bottom-auto
              sm:left-auto
              sm:right-0
              sm:top-full
              sm:mt-2
              sm:w-52
              sm:rounded-xl
            "
            style={{
              zIndex: 30,

              border: `1px solid ${colors.gray100}`,
            }}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3 sm:hidden">
              <span className="text-sm font-extrabold text-gray-950">
                Share article
              </span>

              <button
                type="button"
                onClick={() =>
                  setOpen(false)
                }
                className="text-sm font-semibold text-gray-500"
              >
                Close
              </button>
            </div>

            {links.map(
              (item) => (
                <a
                  key={
                    item.label
                  }
                  href={
                    item.href
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    setOpen(false)
                  }
                  className="
                    block
                    px-5
                    py-3.5
                    text-sm
                    font-semibold
                    text-gray-700
                    transition
                    hover:bg-gray-50
                    hover:text-black
                  "
                >
                  {
                    item.label
                  }
                </a>
              )
            )}

            <div className="border-t border-gray-100">
              <button
                type="button"
                onClick={
                  handleCopy
                }
                className="
                  flex
                  w-full
                  items-center
                  gap-3
                  px-5
                  py-3.5
                  text-left
                  text-sm
                  font-semibold
                  text-gray-700
                  transition
                  hover:bg-gray-50
                  hover:text-black
                "
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}

                {copied
                  ? "Copied!"
                  : "Copy link"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Table Of Contents                                                   */
/* ------------------------------------------------------------------ */

function TableOfContents({
  items,
  activeId,
}) {
  const [
    openIds,
    setOpenIds,
  ] = useState({});

  if (!items.length) {
    return null;
  }

  return (
    <nav aria-label="Table of contents">
      <p
        className="mb-0 text-[17px] font-bold"
        style={{
          color:
            colors.gray950,
        }}
      >
        In this Article
      </p>

      <hr
        className="my-2"
        style={{
          borderColor:
            colors.gray300,
        }}
      />

      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {items.map(
          (item) => {
            const hasChildren =
              item.children
                ?.length > 0;

            const childActive =
              hasChildren &&
              item.children.some(
                (child) =>
                  child.id ===
                  activeId
              );

            const isOpen =
              openIds[
                item.id
              ] ??
              childActive;

            const isActive =
              activeId ===
              item.id;

            return (
              <li
                key={
                  item.id
                }
              >
                <div className="flex items-center gap-3">
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() =>
                        setOpenIds(
                          (
                            prev
                          ) => ({
                            ...prev,

                            [item.id]:
                              !isOpen,
                          })
                        )
                      }
                      aria-label={
                        isOpen
                          ? "Collapse section"
                          : "Expand section"
                      }
                      className="
                        flex
                        h-6
                        w-6
                        flex-shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border
                        bg-white
                        p-0
                      "
                      style={{
                        color:
                          colors.red600,
                        borderColor:
                          colors.red600,
                      }}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <span
                      className="
                        flex
                        h-6
                        w-6
                        flex-shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border
                        bg-white
                      "
                      aria-hidden="true"
                      style={{
                        color:
                          colors.red600,
                        borderColor:
                          colors.red600,
                      }}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </span>
                  )}

                  <a
                    href={`#${item.id}`}
                    onClick={(
                      event
                    ) => {
                      event.preventDefault();

                      scrollToHeading(
                        item.id
                      );
                    }}
                    className="
                      min-w-0
                      flex-grow
                      truncate
                      py-1
                      text-[13.5px]
                      no-underline
                      transition
                      hover:underline
                    "
                    style={{
                      lineHeight:
                        1.3,

                      fontWeight:
                        isActive
                          ? 700
                          : 500,

                      color:
                        isActive
                          ? colors.gray950
                          : colors.gray600,
                    }}
                  >
                    {
                      item.text
                    }
                  </a>
                </div>

                {hasChildren &&
                  isOpen && (
                    <ul
                      className="mt-1 flex list-none flex-col gap-1"
                      style={{
                        marginLeft:
                          24,

                        paddingLeft:
                          12,

                        borderLeft: `1px solid ${colors.blue200}`,
                      }}
                    >
                      {item.children.map(
                        (
                          child
                        ) => {
                          const childIsActive =
                            child.id ===
                            activeId;

                          return (
                            <li
                              key={
                                child.id
                              }
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  aria-hidden="true"
                                  className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                                  style={{
                                    backgroundColor:
                                      childIsActive
                                        ? colors.red600
                                        : colors.gray400,
                                  }}
                                />
                                <a
                                  href={`#${child.id}`}
                                  onClick={(
                                    event
                                  ) => {
                                    event.preventDefault();

                                    scrollToHeading(
                                      child.id
                                    );
                                  }}
                                  className="
                                    block
                                    min-w-0
                                    flex-grow
                                    truncate
                                    py-1
                                    text-[13px]
                                    no-underline
                                    transition
                                    hover:underline
                                  "
                                  style={{
                                    lineHeight:
                                      1.3,

                                    fontWeight:
                                      childIsActive
                                        ? 700
                                        : 400,

                                    color:
                                      childIsActive
                                        ? colors.gray950
                                        : colors.gray500,
                                  }}
                                >
                                  {
                                    child.text
                                  }
                                </a>
                              </div>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  )}
              </li>
            );
          }
        )}
      </ul>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Article Content                                                     */
/* ------------------------------------------------------------------ */

function BlogContentBlock({
  item,
  isFirstParagraph,
}) {
  if (
    item.type ===
    "paragraph"
  ) {
    const text =
      String(
        item.text || ""
      );

    return (
      <p
        className="mb-2"
        style={{
          fontSize: 17,

          lineHeight:
            1.55,

          color:
            colors.gray700,
        }}
      >
        {isFirstParagraph &&
        text ? (
          <>
            <span
              style={{
                float: "left",

                marginRight:
                  6,

                fontFamily:
                  "Georgia, 'Times New Roman', serif",

                fontSize:
                  58,

                fontWeight:
                  700,

                lineHeight:
                  0.85,

                color:
                  colors.gray950,
              }}
            >
              {
                text.charAt(
                  0
                )
              }
            </span>

            {renderInlineContent(
              text.slice(1)
            )}
          </>
        ) : (
          renderInlineContent(
            text
          )
        )}
      </p>
    );
  }

  if (
    item.type ===
    "heading"
  ) {
    const level =
      Math.min(
        6,
        Math.max(
          2,
          item.level ||
            2
        )
      );

    const HeadingTag =
      `h${level}`;

    const sizes = {
      2: 26,
      3: 22,
      4: 19,
      5: 17,
      6: 16,
    };

    const colorsByLevel =
      {
        2: colors.gray950,
        3: colors.gray950,
        4: colors.gray900,
        5: colors.gray800,
        6: colors.gray700,
      };

    return (
      <HeadingTag
        id={item.id}
        style={{
          scrollMarginTop:
            "calc(var(--hl-site-header-height) + 24px)",

          marginTop:
            level === 2
              ? 18
              : level === 3
                ? 16
                : 14,

          marginBottom:
            6,

          fontFamily:
            level <= 3
              ? "Georgia, 'Times New Roman', serif"
              : undefined,

          fontSize:
            sizes[level],

          fontWeight:
            700,

          letterSpacing:
            "-0.01em",

          color:
            colorsByLevel[
              level
            ],
        }}
      >
        {renderInlineContent(item.text)}
      </HeadingTag>
    );
  }

  if (
    item.type === "list"
  ) {
    const ListTag =
      item.style ===
      "ordered"
        ? "ol"
        : "ul";

    return (
      <ListTag
        className="mb-2"
        style={{
          paddingLeft:
            24,

          fontSize:
            17,

          lineHeight:
            1.55,

          color:
            colors.gray700,

          display:
            "flex",

          flexDirection:
            "column",

          gap: 2,
        }}
      >
        {(item.items || []).map(
          (
            listItem,
            index
          ) => (
            <li
              key={
                index
              }
              style={{
                paddingLeft:
                  4,
              }}
            >
              {renderInlineContent(
                listItem
              )}
            </li>
          )
        )}
      </ListTag>
    );
  }

  if (
    item.type === "link"
  ) {
    const href = String(item.href || "");
    const external =
      /^https?:\/\//i.test(href);

    return (
      <p
        className="mb-2"
        style={{
          fontSize: 17,
          lineHeight: 1.55,
        }}
      >
        <a
          href={href}
          {...(
            external
              ? {
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {}
          )}
          style={{
            ...brandGradientTextStyle,
            fontWeight: 700,
            textDecoration: "underline",
            textUnderlineOffset: 4,
          }}
        >
          {item.text || href}
        </a>
      </p>
    );
  }

  if (
    item.type ===
    "quote"
  ) {
    return (
      <blockquote
        className="my-3"
        style={{
          position:
            "relative",

          borderLeft:
            `2px solid ${colors.red600}`,

          paddingLeft:
            32,

          paddingRight:
            8,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position:
              "absolute",

            left: -4,

            top: -8,

            userSelect:
              "none",

            fontFamily:
              "Georgia, 'Times New Roman', serif",

            fontSize:
              48,

            lineHeight:
              1,

            color:
              colors.gray200,
          }}
        >
          &ldquo;
        </span>

        <p
          style={{
            fontFamily:
              "Georgia, 'Times New Roman', serif",

            fontSize:
              21,

            fontWeight:
              500,

            fontStyle:
              "italic",

            lineHeight:
              1.6,

            color:
              colors.gray900,

            marginBottom:
              0,
          }}
        >
          {renderInlineContent(
            item.text
          )}
        </p>
      </blockquote>
    );
  }

  if (
    item.type ===
    "image"
  ) {
    return (
      <figure className="my-3 overflow-hidden rounded-lg">
        <img
          src={item.src}
          alt={
            item.alt ||
            ""
          }
          className="block h-auto w-full rounded-lg"
        />

        {item.alt ? (
          <figcaption
            className="mt-2 text-center"
            style={{
              fontSize:
                13,

              fontWeight:
                500,

              color:
                colors.gray400,
            }}
          >
            {
              item.alt
            }
          </figcaption>
        ) : null}
      </figure>
    );
  }

  if (
    item.type ===
    "code"
  ) {
    return (
      <div
        className="my-3 overflow-hidden rounded-lg"
        style={{
          border:
            "1px solid #1f1f1f",

          backgroundColor:
            "#0a0a0f",

          boxShadow:
            "0 1px 2px rgba(0,0,0,0.1)",
        }}
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
          {[0, 1, 2].map(
            (dot) => (
              <span
                key={dot}
                className="h-2.5 w-2.5 rounded-full"
                style={{
                  backgroundColor:
                    colors.gray600,
                }}
              />
            )
          )}
        </div>

        <pre
          className="m-0 overflow-x-auto p-4"
          style={{
            fontSize:
              13.5,

            lineHeight:
              1.75,

            color:
              colors.gray100,
          }}
        >
          <code
            style={{
              fontFamily:
                "SFMono-Regular, Menlo, Consolas, monospace",
            }}
          >
            {
              item.code ||
              item.text
            }
          </code>
        </pre>
      </div>
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */

function BlogFAQAccordion({
  faqs,
}) {
  const [
    activeIndex,
    setActiveIndex,
  ] = useState(null);

  return (
    <section
      className="mt-4 border-t pt-3"
      style={{
        borderColor:
          colors.gray200,
      }}
    >
      <h2
        style={{
          fontFamily:
            "Georgia, 'Times New Roman', serif",

          fontSize:
            26,

          fontWeight:
            700,

          letterSpacing:
            "-0.01em",

          color:
            colors.gray950,
        }}
      >
        Frequently asked questions
      </h2>

      <div
        className="mt-3 border-b"
        style={{
          borderColor:
            colors.gray200,
        }}
      >
        {faqs.map(
          (
            faq,
            index
          ) => {
            const isOpen =
              activeIndex ===
              index;

            return (
              <div
                key={index}
                style={{
                  borderTop:
                    index === 0
                      ? "none"
                      : `1px solid ${colors.gray200}`,
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setActiveIndex(
                      (
                        prev
                      ) =>
                        prev ===
                        index
                          ? null
                          : index
                    )
                  }
                  aria-expanded={
                    isOpen
                  }
                  className="flex w-full items-center justify-between gap-4 bg-transparent py-4 text-left"
                >
                  <span
                    style={{
                      fontSize:
                        15,

                      fontWeight:
                        700,

                      color:
                        colors.gray950,
                    }}
                  >
                    {
                      faq.question
                    }
                  </span>

                  <span
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition"
                    style={{
                      border: `1px solid ${
                        isOpen
                          ? colors.gray900
                          : colors.gray300
                      }`,

                      color:
                        isOpen
                          ? colors.gray900
                          : colors.gray500,

                      transform:
                        isOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </span>
                </button>

                <div
                  style={{
                    overflow:
                      "hidden",

                    maxHeight:
                      isOpen
                        ? 500
                        : 0,

                    opacity:
                      isOpen
                        ? 1
                        : 0,

                    transition:
                      "max-height 0.3s ease, opacity 0.3s ease",
                  }}
                >
                  <p
                    className="pb-4 pr-8"
                    style={{
                      fontSize:
                        15,

                      lineHeight:
                        1.7,

                      color:
                        colors.gray600,
                    }}
                  >
                    {
                      faq.answer
                    }
                  </p>
                </div>
              </div>
            );
          }
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* MAIN PAGE                                                           */
/* ------------------------------------------------------------------ */

export default function BlogDetailPage({
  params,
}) {
  const { slug } =
    params;

  const locale =
    normalizeLocale(
      params?.locale
    );

  const [
    post,
    setPost,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    activeId,
    setActiveId,
  ] = useState("");

  const [
    progress,
    setProgress,
  ] = useState(0);

  /* Fetch blog */

  useEffect(() => {
    let active = true;

    setLoading(true);

    getBlog(slug).then(
      (data) => {
        if (active) {
          setPost(data);

          setLoading(
            false
          );
        }
      }
    );

    return () => {
      active = false;
    };
  }, [slug]);

  /* Blocks */

  const blocks =
    useMemo(
      () =>
        withHeadingIds(
          Array.isArray(
            post?.content
          )
            ? post.content
            : []
        ),
      [post]
    );

  /* TOC */

  const tocItems =
    useMemo(
      () =>
        buildToc(
          blocks
        ),
      [blocks]
    );

  const nestedToc =
    useMemo(
      () =>
        nestToc(
          tocItems
        ),
      [tocItems]
    );

  const hasToc =
    tocItems.length >
    0;

  /* Intro text */

  const introSnippet =
    useMemo(
      () =>
        blocks.find(
          (block) =>
            block.type ===
            "paragraph"
        )?.text || "",
      [blocks]
    );

  /* Reading progress + active heading */

  useEffect(() => {
    if (
      loading ||
      !post
    ) {
      return;
    }

    const handleScroll =
      () => {
        const docHeight =
          document
            .documentElement
            .scrollHeight -
          window.innerHeight;

        setProgress(
          docHeight > 0
            ? Math.min(
                100,
                Math.max(
                  0,
                  (window.scrollY /
                    docHeight) *
                    100
                )
              )
            : 0
        );
      };

    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );

    handleScroll();

    let observer;

    if (hasToc) {
      const visibleIds =
        new Set();

      observer =
        new IntersectionObserver(
          (entries) => {
            entries.forEach(
              (entry) => {
                if (
                  entry.isIntersecting
                ) {
                  visibleIds.add(
                    entry
                      .target
                      .id
                  );
                } else {
                  visibleIds.delete(
                    entry
                      .target
                      .id
                  );
                }
              }
            );

            const topmost =
              tocItems.find(
                ({ id }) =>
                  visibleIds.has(
                    id
                  )
              );

            if (topmost) {
              setActiveId(
                topmost.id
              );
            }
          },

          {
            rootMargin:
              "-110px 0px -55% 0px",

            threshold: 0,
          }
        );

      tocItems.forEach(
        ({ id }) => {
          const element =
            document.getElementById(
              id
            );

          if (element) {
            observer.observe(
              element
            );
          }
        }
      );
    }

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );

      observer?.disconnect();
    };
  }, [
    loading,
    post,
    hasToc,
    tocItems,
  ]);

  /* ---------------------------------------------------------------- */
  /* SEO                                                              */
  /* ---------------------------------------------------------------- */

  const canonicalUrl =
    sitePageUrl(
      locale,
      "blog",
      slug
    );

  const title =
    post?.metaTitle ||
    post?.title ||
    "Blog | Highlandgroup";

  const description =
    post?.metaDescription ||
    "Read the latest insights, ideas, and updates from Highland Group.";

  const ogTitle =
    post?.ogTitle ||
    title;

  const ogDescription =
    post?.ogDescription ||
    description;

  const ogImageSource =
    post?.ogImage ||
    post?.featuredImage;

  const ogImage =
    ogImageSource
      ? `${SITE_URL}${
          ogImageSource.startsWith(
            "/"
          )
            ? ""
            : "/"
        }${ogImageSource}`
      : `${SITE_URL}/logo.png`;

  const structuredDataJson =
    post
      ? JSON.stringify({
          "@context":
            "https://schema.org",

          "@type":
            "BlogPosting",

          headline:
            title,

          description,

          image:
            ogImage,

          datePublished:
            post.publishedDate ||
            undefined,

          dateModified:
            post.updatedDate ||
            post.publishedDate ||
            undefined,

          mainEntityOfPage:
            canonicalUrl,

          author: {
            "@type":
              "Person",

            name:
              post.authorName ||
              "Highland Group",
          },

          publisher: {
            "@type":
              "Organization",

            name:
              "Highland Group",

            logo: {
              "@type":
                "ImageObject",

              url: `${SITE_URL}/logo.png`,
            },
          },
        })
      : JSON.stringify({
          "@context":
            "https://schema.org",

          "@type":
            "WebPage",

          url:
            canonicalUrl,

          name:
            title,

          description,
        });

  /* Not found */

  if (
    !loading &&
    !post
  ) {
    return (
      <RootNotFound />
    );
  }

  /* Category */

  const categoryLabel =
    (
      Array.isArray(
        post?.subcategory
      )
        ? post.subcategory.join(
            ", "
          )
        : post?.subcategory
    ) ||
    post?.category ||
    (
      Array.isArray(
        post?.tags
      )
        ? post.tags[0]
        : post?.tags
    ) ||
    "";

  const primaryCategory =
    String(
      categoryLabel
    )
      .split(",")
      .map((value) =>
        value.trim()
      )
      .filter(Boolean)[0];

  /* Author */

  const authorName =
    post?.author?.name ||
    post?.authorName;

  const authorAvatar =
    post?.author?.avatar ||
    post?.author?.image ||
    post?.authorAvatar;

  let firstParagraphSeen =
    false;

  return (
    <>
      {/* ------------------------------------------------------------ */}
      {/* SEO                                                          */}
      {/* ------------------------------------------------------------ */}

      <Helmet>
        <title>
          {title}
        </title>

        <meta
          name="description"
          content={
            description
          }
        />

        {post?.keywords && (
          <meta
            name="keywords"
            content={
              post.keywords
            }
          />
        )}

        <meta
          name="robots"
          content="index, follow"
        />

        <link
          rel="canonical"
          href={
            canonicalUrl
          }
        />

        <meta
          property="og:title"
          content={
            ogTitle
          }
        />

        <meta
          property="og:description"
          content={
            ogDescription
          }
        />

        <meta
          property="og:type"
          content={
            post
              ? "article"
              : "website"
          }
        />

        <meta
          property="og:url"
          content={
            canonicalUrl
          }
        />

        <meta
          property="og:locale"
          content={ogLocaleTag(
            locale
          )}
        />

        <meta
          property="og:site_name"
          content="Highlandgroup"
        />

        <meta
          property="og:image"
          content={
            ogImage
          }
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content={
            ogTitle
          }
        />

        <meta
          name="twitter:description"
          content={
            ogDescription
          }
        />

        <meta
          name="twitter:image"
          content={
            ogImage
          }
        />

        <script type="application/ld+json">
          {
            structuredDataJson
          }
        </script>
      </Helmet>

      {/* ------------------------------------------------------------ */}
      {/* LOADING                                                      */}
      {/* ------------------------------------------------------------ */}

      {loading ? (
        <main className="hl-blog-page flex min-h-screen items-center justify-center bg-white px-6">
          <p className="text-center text-gray-500">
            Loading article…
          </p>
        </main>
      ) : (
        /* ---------------------------------------------------------- */
        /* BLOG PAGE                                                  */
        /* ---------------------------------------------------------- */

        <main
          className="hl-blog-page min-h-screen bg-white"
          style={{
            position:
              "relative",

            zIndex: 0,

            fontFamily:
              "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

            color:
              colors.gray900,
          }}
        >
          {/* -------------------------------------------------------- */}
          {/* READING PROGRESS                                        */}
          {/* -------------------------------------------------------- */}

          <div
            className="hl-reading-progress"
            aria-hidden="true"
          >
            <div
              className="h-full transition-[width] duration-100"
              style={{
                width: `${progress}%`,

                backgroundImage:
                  brandGradient,
              }}
            />
          </div>

          {/* -------------------------------------------------------- */}
          {/* PAGE CONTENT                                             */}
          {/* -------------------------------------------------------- */}

          <div className="hl-blog-content-wrap mx-auto w-full px-5 pb-10 pt-8 sm:px-8 lg:px-12 xl:px-16">
            {/* ------------------------------------------------------ */}
            {/* HERO / ARTICLE HEADER                                  */}
            {/* ------------------------------------------------------ */}

            <header
              className="
                hl-ox-header
                relative
                grid
                grid-cols-1
                items-stretch
                gap-8
                lg:grid-cols-2
                lg:gap-10
              "
              style={{
                zIndex: 1,
              }}
            >
              {/* LEFT COLUMN */}

              <div className="flex h-full flex-col justify-center">
                {/* Breadcrumb + Share */}

                <div className="flex items-center justify-between gap-3">
                  <nav
                    aria-label="Breadcrumb"
                    className="
                      flex
                      min-w-0
                      items-center
                      gap-2
                      text-sm
                      font-bold
                    "
                    style={{
                      color:
                        colors.gray500,
                    }}
                  >
                    <a
                      href="/blog"
                      className="whitespace-nowrap no-underline"
                      style={{
                        color:
                          colors.red600,
                      }}
                    >
                      Back to blogs
                    </a>

                    {primaryCategory &&
                    primaryCategory !==
                      "Uncategorized" ? (
                      <>
                        <span
                          aria-hidden="true"
                          style={{
                            color:
                              colors.gray300,
                          }}
                        >
                          ›
                        </span>

                        <span
                          className="truncate"
                          style={{
                            color:
                              colors.gray950,
                          }}
                        >
                          {
                            primaryCategory
                          }
                        </span>
                      </>
                    ) : null}
                  </nav>

                  <ShareLinks
                    title={
                      post?.title
                    }
                  />
                </div>

                {/* Title */}

                <h1
                  className="mt-3"
                  style={{
                    fontSize:
                      "clamp(1.5rem, 2.2vw, 2.25rem)",

                    fontWeight:
                      800,

                    lineHeight:
                      1.12,

                    letterSpacing:
                      "-0.02em",

                    color:
                      colors.gray950,
                  }}
                >
                  {post.title}
                </h1>

                {/* Description */}

                {(post.metaDescription ||
                  post.excerpt) && (
                  <p
                    className="mb-0 mt-3"
                    style={{
                      fontSize:
                        16,

                      lineHeight:
                        1.6,

                      color:
                        colors.gray500,
                    }}
                  >
                    {post.metaDescription ||
                      post.excerpt}
                  </p>
                )}

                {/* Intro */}

                {introSnippet ? (
                  <p
                    className="mb-3 mt-2"
                    style={{
                      fontSize:
                        16,

                      lineHeight:
                        1.55,

                      color:
                        colors.gray500,

                      ...lineClamp(
                        6
                      ),
                    }}
                  >
                    {renderInlineContent(
                      introSnippet
                    )}
                  </p>
                ) : null}

                {/* Author */}

                <div className="mt-3">
                  <hr
                    style={{
                      borderColor:
                        colors.gray200,

                      margin: 0,
                    }}
                  />

                  <div className="flex items-center gap-3 py-3">
                    {authorAvatar ? (
                      <img
                        src={
                          authorAvatar
                        }
                        alt={
                          authorName ||
                          ""
                        }
                        className="
                          h-11
                          w-11
                          flex-shrink-0
                          rounded-full
                          object-cover
                        "
                        style={{
                          boxShadow:
                            "0 0 0 2px #fff, 0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      />
                    ) : authorName ? (
                      <div
                        className="
                          flex
                          h-11
                          w-11
                          flex-shrink-0
                          items-center
                          justify-center
                          rounded-full
                          text-sm
                          font-bold
                          text-white
                        "
                        style={{
                          backgroundColor:
                            colors.red600,
                        }}
                      >
                        {authorName
                          .charAt(
                            0
                          )
                          .toUpperCase()}
                      </div>
                    ) : null}

                    <div className="text-sm">
                      {authorName ? (
                        <p
                          className="mb-0 font-bold"
                          style={{
                            color:
                              colors.gray950,
                          }}
                        >
                          {
                            authorName
                          }
                        </p>
                      ) : null}

                      {post.publishedDate ? (
                        <p
                          className="mb-0 mt-1"
                          style={{
                            color:
                              colors.gray500,
                          }}
                        >
                          {formatBlogDate(
                            post.publishedDate
                          )}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <hr
                    style={{
                      borderColor:
                        colors.gray200,

                      margin: 0,
                    }}
                  />
                </div>
              </div>

              {/* RIGHT COLUMN */}

              {post.featuredImage ? (
                <div className="flex min-w-0 flex-col">
                  <div
                    className="hl-ox-featured-image-wrap w-full overflow-hidden rounded-lg"
                    style={{
                      backgroundColor:
                        colors.gray100,
                    }}
                  >
                    <img
                      src={
                        post.featuredImage
                      }
                      alt={
                        post.featuredImageAlt ||
                        post.title
                      }
                      className="block w-full"
                      style={{
                        width:
                          "100%",

                        height:
                          "auto",
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </header>

            {/* ------------------------------------------------------ */}
            {/* ARTICLE                                                */}
            {/* ------------------------------------------------------ */}

            <article className="relative z-0 mt-8 w-full">
              {/* TABLE OF CONTENTS */}

              {hasToc ? (
                <div
                  className="
                    mb-5
                    w-full
                    rounded-lg
                    p-5
                    lg:max-w-[75%]
                  "
                  style={{
                    border:
                      "1px solid rgba(0,0,0,0.1)",

                    backgroundColor:
                      colors.blue50,
                  }}
                >
                  <TableOfContents
                    items={
                      nestedToc
                    }
                    activeId={
                      activeId
                    }
                  />
                </div>
              ) : null}

              {/* BODY */}

              <div className="max-w-none">
                {blocks.map(
                  (
                    item,
                    index
                  ) => {
                    const isFirstParagraph =
                      item.type ===
                        "paragraph" &&
                      !firstParagraphSeen;

                    if (
                      isFirstParagraph
                    ) {
                      firstParagraphSeen =
                        true;
                    }

                    return (
                      <BlogContentBlock
                        key={
                          index
                        }
                        item={
                          item
                        }
                        isFirstParagraph={
                          isFirstParagraph
                        }
                      />
                    );
                  }
                )}
              </div>

              {/* FAQ */}

              {Array.isArray(
                post.faqs
              ) &&
              post.faqs.length >
                0 ? (
                <BlogFAQAccordion
                  faqs={
                    post.faqs
                  }
                />
              ) : null}
            </article>
          </div>

          {/* -------------------------------------------------------- */}
          {/* RESPONSIVE CSS                                           */}
          {/* -------------------------------------------------------- */}

          <style>{`

            /*
            ==========================================================
            GLOBAL SITE HEADER FIX
            ==========================================================

            Desktop header assumed height = 88px
            Mobile header assumed height  = 72px

            Blog page starts AFTER the global fixed navbar.
            */

            .hl-blog-page {
              --hl-site-header-height: 88px;

              position: relative !important;
              z-index: 0 !important;

              padding-top: var(--hl-site-header-height);

              isolation: isolate;
            }


            /*
            ==========================================================
            READING PROGRESS
            ==========================================================

            Progress bar is now BELOW the website navbar
            instead of appearing on top of it.
            */

            .hl-reading-progress {
              position: fixed;

              top: var(--hl-site-header-height);

              left: 0;

              width: 100%;

              height: 2px;

              z-index: 20;

              background-color: ${colors.gray100};

              pointer-events: none;
            }


            /*
            ==========================================================
            HERO
            ==========================================================
            */

            .hl-ox-header {
              position: relative;
              z-index: 1;
            }


            /*
            ==========================================================
            FEATURED IMAGE
            ==========================================================
            */

            .hl-ox-featured-image-wrap {
              width: 100%;

              height: auto;

              overflow: hidden;

              border-radius: 8px;

              position: relative;

              z-index: 0;
            }

            .hl-ox-featured-image-wrap img {
              display: block;

              width: 100%;

              height: auto;
            }


            /*
            ==========================================================
            TABLET / DESKTOP
            ==========================================================
            */

            @media (min-width: 992px) {

              .hl-ox-header {
                min-height: 25rem;
              }

            }


            /*
            ==========================================================
            MOBILE HEADER
            ==========================================================
            */

            @media (max-width: 767.98px) {

              .hl-blog-page {
                --hl-site-header-height: 72px;
              }

              .hl-blog-page .hl-blog-content-wrap {
                padding-top: 2rem;
              }

            }


            /*
            ==========================================================
            SMALL MOBILE
            ==========================================================
            */

            @media (max-width: 575.98px) {

            }

          `}</style>
        </main>
      )}
    </>
  );
}
